// engine/rng.ts — seeded mulberry32. One run-scoped stream, reproducible from ?seed=.

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return function () {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export class Rng {
  seed: number
  private fn: () => number

  constructor(seed: number) {
    this.seed = seed >>> 0
    this.fn = mulberry32(this.seed)
  }

  reseed(seed: number): void {
    this.seed = seed >>> 0
    this.fn = mulberry32(this.seed)
  }

  next(): number {
    return this.fn()
  }

  /** [a, b) */
  range(a: number, b: number): number {
    return a + this.fn() * (b - a)
  }

  /** [a, b] inclusive integers */
  int(a: number, b: number): number {
    if (b < a) return a
    return a + Math.floor(this.fn() * (b - a + 1))
  }

  chance(p: number): boolean {
    return this.fn() < p
  }

  pick<T>(arr: readonly T[]): T {
    return arr[Math.floor(this.fn() * arr.length)]
  }

  angle(): number {
    return this.fn() * Math.PI * 2
  }

  /** Fisher-Yates into a caller-owned array (avoids allocation when reused). */
  shuffleInto<T>(src: readonly T[], out: T[]): T[] {
    out.length = 0
    for (let i = 0; i < src.length; i++) out.push(src[i])
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(this.fn() * (i + 1))
      const t = out[i]
      out[i] = out[j]
      out[j] = t
    }
    return out
  }
}

/** The run-scoped stream. Reseeded at the start of every run. */
export const runRng = new Rng(1)

function queryParam(name: string): string | null {
  try {
    return new URLSearchParams(window.location.search).get(name)
  } catch {
    return null
  }
}

/** ?seed=N — a fixed seed for every run this session (bot/debug), else null. */
export function fixedSeedFromQuery(): number | null {
  const raw = queryParam('seed')
  if (raw == null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n >>> 0 : null
}

export function hasQueryFlag(name: string): boolean {
  const raw = queryParam(name)
  return raw === '1' || raw === 'true'
}

export function queryString(name: string): string | null {
  return queryParam(name)
}

export function queryNumber(name: string): number | null {
  const raw = queryParam(name)
  if (raw == null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

/** Fresh entropy seed for a normal play run. */
export function entropySeed(): number {
  try {
    const buf = new Uint32Array(1)
    crypto.getRandomValues(buf)
    return buf[0] >>> 0
  } catch {
    return (Date.now() ^ (Math.random() * 0xffffffff)) >>> 0
  }
}
